import { redirect } from "@tanstack/react-router";
import type {
  ClientPageErrorData,
  PageInitialData,
  PageInitialDataResult,
  PageRedirectData,
  PageServerMessageData,
} from "./pageInitialDataTypes";
import { normalizeServerMessage, stashRedirectMessagesIfNeeded } from "./normalizeServerMessage";
import { getStore } from "~/app/store/getStore";
import { addMessages } from "~/features/toasts/redux/actions";

export class PageInitialDataError extends Error {
  readonly pageError?: ClientPageErrorData;

  constructor(pageError?: ClientPageErrorData) {
    super(pageError?.message ?? pageError?.messageKey ?? pageError?.code ?? "Page initial data request failed");
    this.name = "PageInitialDataError";
    this.pageError = pageError;
  }
}

export function unwrapPageInitialData<TData>(result: PageInitialDataResult<TData>): PageInitialData<TData> {
  switch (result.status) {
    case "OK": {
      // Keep the runtime guard even though the wire type requires metadata:
      // network payloads are not made valid by their TypeScript annotation.
      if (!result.metadata) {
        throw new PageInitialDataError({
          code: "INVALID_OK_PAGE_INITIAL_DATA",
          message: "OK page initial data must include metadata.",
        });
      }

      return {
        data: result.data,
        metadata: result.metadata,
      } as PageInitialData<TData>;
    }
    case "REDIRECT": {
      surfaceRedirectServerMessages(result.redirect);
      throw redirect({
        href: result.redirect.location,
        statusCode: result.redirect.status ?? 302,
      });
    }
    case "ERROR":
      throw new PageInitialDataError(result.error);
    default:
      throw new PageInitialDataError({
        code: "UNKNOWN_PAGE_INITIAL_DATA_STATUS",
        message: `Unknown page initial data status: ${String((result as { status?: unknown }).status)}`,
      });
  }
}

/**
 * Surfaces server messages carried inline on a REDIRECT result before navigation proceeds:
 * dispatches them as toasts, and stashes them to sessionStorage when the target is not a
 * TanStack-owned route (the SPA unloads on a full-page navigation to an external/absolute URL).
 * This is the FlashMap replacement that also works under a future headless/OCC backend.
 */
function surfaceRedirectServerMessages(redirect: PageRedirectData) {
  const messages: PageServerMessageData[] = redirect.serverMessages;
  if (!messages.length) {
    return;
  }
  getStore().dispatch(addMessages(messages.map(normalizeServerMessage)));
  stashRedirectMessagesIfNeeded(redirect.location, messages);
}
